import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Handles the Emergent Google Auth redirect landing.
 * Emergent OAuth returns the browser to /auth/callback#session_id=<token>.
 * We exchange that for our own JWT + user record, then bounce to /dashboard.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUserFromGoogle } = useAuth();
  const [status, setStatus] = useState("Signing you in…");
  const ran = useRef(false); // StrictMode guard — don't double-exchange the same session_id

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      // Parse #session_id=... from URL fragment
      const raw = window.location.hash || "";
      const params = new URLSearchParams(raw.startsWith("#") ? raw.slice(1) : raw);
      const sessionId = params.get("session_id");

      if (!sessionId) {
        setStatus("Missing session — redirecting to sign-in…");
        setTimeout(() => navigate("/login", { replace: true }), 1200);
        return;
      }

      try {
        const { data } = await api.post("/auth/emergent-session", {
          session_id: sessionId,
        });
        localStorage.setItem("stock_token", data.token);
        localStorage.setItem("stock_user", JSON.stringify(data.user));
        if (typeof setUserFromGoogle === "function") setUserFromGoogle(data.user);
        // Clear the hash so refresh doesn't re-exchange
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Sign-in failed. Please try again.";
        setStatus(msg);
        setTimeout(() => navigate("/login", { replace: true }), 2500);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-bone-50"
      data-testid="auth-callback-page"
    >
      <div className="text-center">
        <div className="overline mb-3">— Emergent Google Auth</div>
        <div className="font-serif-display text-2xl text-stone_brand-dark mb-3">
          {status}
        </div>
        <div className="w-6 h-6 border-2 border-moss border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    </div>
  );
}
